import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../components/Navbar/Navbar";
import contactImg from "../../assets/images/contact.png";
import Hero from "../../components/Hero/Hero";
import Footer from "../../components/Footer/Footer";
import EmailIcon from "@mui/icons-material/Email";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MapIcon from "@mui/icons-material/Map";
import SendIcon from "@mui/icons-material/Send";
import classes from "./Contact.module.scss";
import ContactModal from "../../components/Modals/ContactModal";

const TURNSTILE_SITE_KEY = "0x4AAAAAADnNr-MmPIed4KBc";

const Contact = () => {
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const phoneRef = useRef(null);
  const messageRef = useRef(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetRef = useRef(null);

  const [state, setState] = useState({
    name: "<Required>",
    email: "<Required>",
    phone: "<Not Entered>",
    message: "<Required>",
    website: "",
    formStartedAt: Date.now(),
    turnstileToken: "",
    sending: false,
    code: "UBG-MOJI",
    dialog: false,
    error: false,
    errorMessage: "",
  });

  useEffect(() => {
    const renderTurnstile = () => {
      if (
        !window.turnstile ||
        !turnstileRef.current ||
        turnstileWidgetRef.current
      ) {
        return;
      }

      turnstileWidgetRef.current = window.turnstile.render(
        turnstileRef.current,
        {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          action: "contact_form",
          callback: (token) => {
            setState((prevState) => ({
              ...prevState,
              turnstileToken: token,
            }));
          },
          "expired-callback": () => {
            setState((prevState) => ({
              ...prevState,
              turnstileToken: "",
            }));
          },
          "error-callback": () => {
            setState((prevState) => ({
              ...prevState,
              turnstileToken: "",
            }));
          },
        },
      );
    };

    if (window.turnstile) {
      renderTurnstile();
      return;
    }

    const existingScript = document.querySelector(
      'script[src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"]',
    );

    if (existingScript) {
      existingScript.addEventListener("load", renderTurnstile);

      return () => {
        existingScript.removeEventListener("load", renderTurnstile);
      };
    }

    const script = document.createElement("script");
    script.src =
      "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = renderTurnstile;

    document.body.appendChild(script);

    return () => {
      script.onload = null;
    };
  }, []);

  const resetTurnstile = () => {
    if (window.turnstile && turnstileWidgetRef.current) {
      window.turnstile.reset(turnstileWidgetRef.current);
    }

    setState((prevState) => ({
      ...prevState,
      turnstileToken: "",
    }));
  };

  const sendEmail = async (e) => {
    e.preventDefault();

    if (state.sending) return;

    const timeTaken = Date.now() - state.formStartedAt;

    if (state.website.length > 0 || timeTaken < 2500) {
      setState((prevState) => ({
        ...prevState,
        error: true,
        errorMessage:
          "Your message could not be submitted. Please refresh the page and try again.",
      }));
      return;
    }

    if (!state.turnstileToken) {
      setState((prevState) => ({
        ...prevState,
        error: true,
        errorMessage:
          "Please complete the verification check before sending your message.",
      }));
      return;
    }

    try {
      setState((prevState) => ({
        ...prevState,
        sending: true,
      }));

      let response = await fetch(
        `https://api.ramteentaheri.com:3838/mailer/submit-form`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            code: state.code,
            name: state.name,
            email: state.email,
            phone: state.phone,
            message: state.message,
            website: state.website,
            formStartedAt: state.formStartedAt,
            turnstileToken: state.turnstileToken,
          }),
        },
      );

      response = await response.json();

      if (!response.success) {
        throw new Error(
          response.message ||
            "There was an issue with the contact form server. Please contact the administrator, or try again later.",
        );
      }

      nameRef.current.value = "";
      emailRef.current.value = "";
      phoneRef.current.value = "";
      messageRef.current.value = "";

      resetTurnstile();

      setState((prevState) => ({
        ...prevState,
        sending: false,
        name: "",
        email: "",
        phone: "",
        message: "",
        website: "",
        formStartedAt: Date.now(),
        dialog: true,
      }));
    } catch (err) {
      resetTurnstile();

      setState((prevState) => ({
        ...prevState,
        sending: false,
        error: true,
        errorMessage: err.message,
      }));
    }
  };

  const handleOnKeyDown = (e) => {
    if (e.keyCode === 13) {
      setState((prevState) => ({
        ...prevState,
        message: prevState.message + "<br />",
      }));
    }
  };

  const handleOnChange = (type, e) => {
    let placeholder = "<Required>";

    if (type === "phone") {
      placeholder = "<Not Entered>";
    }

    setState((prevState) => ({
      ...prevState,
      [type]: e.target.value.length > 0 ? e.target.value : placeholder,
    }));
  };

  const handleClose = () => {
    setState((prevState) => ({
      ...prevState,
      dialog: false,
      error: false,
      errorMessage: "",
    }));
  };

  return (
    <div className={classes["contact"]}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>Contact | Unified Building Group</title>
        <meta
          name="description"
          content="Contact Unified Building Group for remedial building, restoration, waterproofing, facade and strata repair enquiries across NSW."
        />
      </Helmet>

      <Navbar />
      <Hero
        title="Contact us"
        slogan="Speak with our remedial building team"
        image={contactImg}
      />

      <section className={classes["contact-section"]}>
        <div className={classes["container"]}>
          <ContactModal
            open={state.dialog}
            handleClose={handleClose}
            title="Message Sent"
            text="Thank you for contacting Unified Building Group. Our team will get back to you as soon as possible."
            buttonText="OK"
            type="success"
          />

          <ContactModal
            open={state.error}
            text={state.errorMessage}
            handleClose={handleClose}
            title="Message Not Sent"
            buttonText="OK"
            type="error"
          />

          <div className={classes["section-header"]}>
            <p>Enquiries</p>
            <h2>Get in touch with us</h2>
            <span>
              Send us a message and our team will respond as soon as possible.
            </span>
          </div>

          <div className={classes["form-container"]}>
            <form className={classes["form-control"]} onSubmit={sendEmail}>
              <input
                className={classes["spam-field"]}
                type="text"
                name="website"
                tabIndex="-1"
                autoComplete="off"
                value={state.website}
                onChange={(e) =>
                  setState((prevState) => ({
                    ...prevState,
                    website: e.target.value,
                  }))
                }
              />

              <label>
                <span>Name</span>
                <input
                  type="text"
                  placeholder="Your name"
                  name="user_name"
                  onChange={(e) => handleOnChange("name", e)}
                  required
                  ref={nameRef}
                  disabled={state.sending}
                />
              </label>

              <label>
                <span>Email</span>
                <input
                  type="email"
                  placeholder="your@email.com"
                  name="user_email"
                  onChange={(e) => handleOnChange("email", e)}
                  required
                  ref={emailRef}
                  disabled={state.sending}
                />
              </label>

              <label>
                <span>Phone</span>
                <input
                  type="text"
                  placeholder="Phone number"
                  name="phone"
                  onChange={(e) => handleOnChange("phone", e)}
                  ref={phoneRef}
                  disabled={state.sending}
                />
              </label>

              <label className={classes["message-field"]}>
                <span>Message</span>
                <textarea
                  name="message"
                  cols={30}
                  rows={8}
                  placeholder="Tell us about your project..."
                  onChange={(e) => handleOnChange("message", e)}
                  onKeyDown={(e) => handleOnKeyDown(e)}
                  required
                  ref={messageRef}
                  disabled={state.sending}
                ></textarea>
              </label>

              <div className={classes["turnstile-wrap"]}>
                <div ref={turnstileRef}></div>
              </div>

              <button
                className={`${classes["send-btn"]} ${
                  state.sending ? classes["sending"] : ""
                }`}
                type="submit"
                disabled={state.sending}
              >
                {!state.sending && (
                  <>
                    <SendIcon />
                    <span>Send Message</span>
                  </>
                )}

                {state.sending && (
                  <>
                    <span className={classes["sending-dots"]}>
                      <i></i>
                      <i></i>
                      <i></i>
                    </span>
                    <span>Sending Message</span>
                  </>
                )}
              </button>
            </form>

            <aside className={classes["contact-details"]}>
              <div className={classes["details-card"]}>
                <h3>Contact Details</h3>

                <div className={classes["detail-row"]}>
                  <EmailIcon />
                  <div>
                    <span>Email</span>
                    <a href="mailto:admin@unifiedbg.com.au">
                      Admin@unifiedbg.com.au
                    </a>
                  </div>
                </div>

                <div className={classes["detail-row"]}>
                  <LocalPhoneIcon />
                  <div>
                    <span>Phone</span>
                    <a href="tel:1800824000">1800-UBG-000 (1800 824 000)</a>
                  </div>
                </div>

                <div className={classes["detail-row"]}>
                  <MapIcon />
                  <div>
                    <span>Address</span>
                    <a
                      href="https://www.google.com/maps/dir//123+Epping+Rd+Macquarie+Park+NSW+2113/@-33.78406,151.1183696,16z/data=!4m5!4m4!1m0!1m2!1m1!1s0x6b12a7a9081591cf:0xcbd40c3940f109de"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Avaya House, Level 9, 123 Epping Rd, Macquarie Park, NSW
                      2113
                    </a>
                  </div>
                </div>
              </div>

              <iframe
                title="Get Directions"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3316.0945896658613!2d151.11349869410796!3d-33.78405544571854!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x6b12a7a9081591cf%3A0xcbd40c3940f109de!2s123%20Epping%20Rd%2C%20Macquarie%20Park%20NSW%202113!5e0!3m2!1sen!2sau!4v1724385879896!5m2!1sen!2sau"
                width="100%"
                height="360"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </aside>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Contact;
